import { type ShouldRevalidateFunctionArgs } from 'react-router';

import { useAppConfig } from '#/config/AppConfigContext';
import { RouteModule } from '#/horizon/components/RouteModule';
import { cmsApiIsomorphicFetchPage } from '#/modules/cms/api/cmsApiFetchPage';
import { getCmsSearchLandingPagePageLayoutHandleProps } from '#/modules/cms/helpers/getCmsSearchLandingPagePageLayoutHandleProps';
import { CmsPagePageLoadable } from '#/modules/cms/pages/Page/CmsPagePageLoadable';
import { getCmsPagePageLayoutHandleProps } from '#/modules/cms/pages/Page/getCmsPagePageLayoutHandleProps';
import { CmsPostPageLoadable } from '#/modules/cms/pages/Post/CmsPostPageLoadable';
import { getCmsPagePostLayoutHandleProps } from '#/modules/cms/pages/Post/getCmsPagePostLayoutHandleProps';
import { CmsSplatHypernovaRedirect } from '#/modules/cms/pages/splat/CmsSplatHypernovaRedirect';
import { cmsSplatRoute } from '#/modules/cms/routing/cms.routes';
import { PostSearchPageLoadable } from '#/modules/postSearch/pages/PostSearch/PostSearchPageLoadable';
import { getPostSearchPageLayoutHandleProps } from '#/modules/postSearch/pages/PostSearch/getPostSearchPageLayoutHandleProps';
import { postSearchRouteReference } from '#/modules/postSearch/routing/postSearch.routes';
import { SearchLandingPageLoadable } from '#/modules/search/pages/Landing/SearchLandingPageLoadable';
import { searchGetRoutePathSwitch } from '#/modules/search/routing/helpers/searchGetRoutePathSwitch';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import { HORIZON_ENABLED } from '#/rendering/state/renderingState';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getAppRouteSearchParams } from '#/routing/utils/getAppRouteSearchParams';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';
import { imageUrlForOgMeta } from '#/utils/filestack';

import type { CmsSplatPageLoaderData } from './CmsSplatPageLoaderData';
import { cmsSplatRouteRevalidation } from './cmsSplatRouteRevalidation';
import { getCmsContentOgImage } from './getCmsContentOgImage';

export const cmsRouteId = 'cms-route';
export const id = cmsRouteId; // only used on hypernova

// Only revalidate if the pathname changes or `?pq=` is added or removed
export function shouldRevalidate({
  currentUrl,
  nextUrl,
}: ShouldRevalidateFunctionArgs): boolean {
  if (cmsSplatRouteRevalidation.force) return true;

  if (currentUrl.pathname !== nextUrl.pathname) return true;
  if (currentUrl.searchParams.has('pq') !== nextUrl.searchParams.has('pq'))
    return true;

  return false;
}

export async function loader({
  request,
  context,
  params: { ['*']: slug },
}: HorizonAppRouteLoaderArgs<typeof cmsSplatRoute>) {
  const searchParams = getAppRouteSearchParams({
    url: request.url,
    route: cmsSplatRoute,
  });

  try {
    const { data: content, ssrLandingPageResults } =
      await cmsApiIsomorphicFetchPage({
        request,
        context,
      })(slug, searchParams.preview === 'true');

    if (content.type === 'CMS_REDIRECT') {
      if (HORIZON_ENABLED) {
        return idealistRedirect({ request, context })(content.url);
      } else {
        return {
          type: 'HYPERNOVA_REDIRECT',
          redirectPath: content.url,
        } satisfies CmsSplatPageLoaderData;
      }
    }

    if (content.type === 'SEARCH_LANDING_PAGE') {
      await SearchLandingPageLoadable.load();
      return {
        type: 'SEARCH_LANDING_PAGE',
        content,
        results: ssrLandingPageResults,
        handle: getCmsSearchLandingPagePageLayoutHandleProps({ content }),
      } satisfies CmsSplatPageLoaderData;
    }

    if (typeof searchParams['pq'] !== 'undefined' && content.subsite.homePage) {
      await PostSearchPageLoadable.load();
      return {
        type: 'POST_SEARCH',
        content,
        handle: getPostSearchPageLayoutHandleProps({ content }),
      } satisfies CmsSplatPageLoaderData;
    }

    if (content.heroSearch?.onlyResults) {
      const redirectPath = searchGetRoutePathSwitch({
        q: typeof searchParams.q === 'string' ? searchParams.q : undefined,
        locale: context.locale,
        filtersByName: content.heroSearch.defaultSearchFilters,
        embedSearchWidgetId: null,
      });

      if (HORIZON_ENABLED) {
        return idealistRedirect({ request, context })(redirectPath);
      } else {
        return {
          type: 'HYPERNOVA_REDIRECT',
          redirectPath,
        } satisfies CmsSplatPageLoaderData;
      }
    }

    if (content.onlySearchResults && content.subsite?.homePage) {
      const redirectPath = postSearchRouteReference.with({
        pq: typeof searchParams.pq === 'string' ? searchParams.pq : '',
        slug: content.url,
      });

      if (HORIZON_ENABLED) {
        return idealistRedirect({ request, context })(redirectPath);
      } else {
        return {
          type: 'HYPERNOVA_REDIRECT',
          redirectPath,
        } satisfies CmsSplatPageLoaderData;
      }
    }

    if (content.type === 'PAGE') {
      await CmsPagePageLoadable.load();

      return {
        type: 'PAGE',
        content,
        handle: getCmsPagePageLayoutHandleProps({ content }),
      } satisfies CmsSplatPageLoaderData;
    }

    // content.type === 'POST'
    await CmsPostPageLoadable.load();

    return {
      type: 'POST',
      content,
      handle: getCmsPagePostLayoutHandleProps({ content }),
    } satisfies CmsSplatPageLoaderData;
  } catch {
    try {
      await NotFoundPageLoadable.load();
    } catch {}

    return idealistNotFound(null);
  }
}

export default function CmsSplatHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { appConfig } = useAppConfig();

  switch (loaderData?.type) {
    case undefined:
      return <NotFoundPageLoadable />;
    case 'PAGE': {
      const image = getCmsContentOgImage(loaderData.content);

      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          ogImage={
            image
              ? {
                  url: imageUrlForOgMeta({ data: image, appConfig }),
                  alt: image.altText ?? null,
                }
              : null
          }
          description={loaderData.content.metaDescription}
          rssFeed={
            loaderData.content.subsite.homePage?.id === loaderData.content.id
              ? {
                  title: `${loaderData.content.headTitle ?? loaderData.content.title} Feed`,
                  href: `${appConfig.idealist.appUrl}/${loaderData.content.url}/feed.rss`,
                }
              : null
          }
          robotsNoIndex={!loaderData.content.visibleToRobots}
          translations={{
            items: [loaderData.content, ...loaderData.content.translations].map(
              (translation) => ({
                url: `${appConfig.idealist.appUrl}/${translation.url}`,
                locale: translation.locale,
              }),
            ),
            xDefault: loaderData.content.xDefaultTranslationUrl,
          }}
        >
          <CmsPagePageLoadable content={loaderData.content} />
        </RouteModule>
      );
    }
    case 'POST': {
      const image = getCmsContentOgImage(loaderData.content);

      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          ogImage={
            image
              ? {
                  url: imageUrlForOgMeta({ data: image, appConfig }),
                  alt: image.altText ?? null,
                }
              : null
          }
          description={loaderData.content.metaDescription}
          rssFeed={
            loaderData.content.subsite.homePage?.id === loaderData.content.id
              ? {
                  title: `${loaderData.content.headTitle ?? loaderData.content.title} Feed`,
                  href: `${appConfig.idealist.appUrl}/${loaderData.content.url}/feed.rss`,
                }
              : null
          }
          robotsNoIndex={!loaderData.content.visibleToRobots}
          article={{
            firstPublished: loaderData.content.articleMeta.firstPublished,
            lastModified: loaderData.content.articleMeta.lastModified,
            author: loaderData.content.author?.name ?? null,
            tags: loaderData.content.tags.map((tag) => tag.tag),
          }}
          translations={{
            items: [loaderData.content, ...loaderData.content.translations].map(
              (translation) => ({
                url: `${appConfig.idealist.appUrl}/${translation.url}`,
                locale: translation.locale,
              }),
            ),
            xDefault: loaderData.content.xDefaultTranslationUrl,
          }}
          schemaMarkups={[loaderData.content.schemaMarkup]}
        >
          <CmsPostPageLoadable content={loaderData.content} />
        </RouteModule>
      );
    }
    case 'SEARCH_LANDING_PAGE':
      return (
        <RouteModule
          title={
            loaderData.content.location?.name
              ? `${loaderData.content.name}, ${loaderData.content.location.name}`
              : loaderData.content.name
          }
          description={loaderData.content.description}
          translations={
            loaderData.content.translations.length > 0
              ? {
                  items: loaderData.content.translations.map((translation) => ({
                    url: `${appConfig.idealist.appUrl}/${translation.url}`,
                    locale: translation.locale,
                  })),
                  xDefault: `${appConfig.idealist.appUrl}/${loaderData.content.translations[0].url}`,
                }
              : null
          }
        >
          <SearchLandingPageLoadable content={loaderData.content} />
        </RouteModule>
      );
    case 'POST_SEARCH':
      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          description={loaderData.content.metaDescription}
        >
          <PostSearchPageLoadable content={loaderData.content} />
        </RouteModule>
      );
    case 'HYPERNOVA_REDIRECT':
      return (
        <CmsSplatHypernovaRedirect redirectPath={loaderData.redirectPath} />
      );
  }
}
